<template>
  <b-container class="bg-white py-2">
    <b-form @submit.prevent="onSaveHandle">
      <b-row class="mb-3">
        <b-col cols="12">
          <div class="float-right">
            <b-button variant="default" @click="$router.back()">
              Cancel
            </b-button>
            <b-btn variant="info" type="submit" :disabled="saveLoading">
              <template v-if="!saveLoading">Save</template>
              <template v-else>
                <b-spinner small />
                <span>Saving...</span>
              </template>
            </b-btn>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-form-group
            horizontal
            label="Email:"
            :invalid-feedback="emailInvalidFeedback"
          >
            <b-form-input
              v-model="$v.email.$model"
              placeholder="Email"
              :state="validateState('email')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            label="Password:"
            :invalid-feedback="passwordInvalidFeedback"
          >
            <b-form-input
              v-model="$v.password.$model"
              type="password"
              placeholder="Password"
              :state="validateState('password')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            label="Confirm Password:"
            :invalid-feedback="confirmPasswordInvalidFeedback"
          >
            <b-form-input
              v-model="$v.confirmPassword.$model"
              type="password"
              placeholder="Confirm Password"
              :state="validateState('confirmPassword')"
            />
          </b-form-group>

          <b-form-group
            horizontal
            label="Send an email to the user with a temporary password:"
          >
            <b-form-checkbox
              class="mt-1"
              switch
              v-model="sendEmail"
              :value="1"
              :unchecked-value="0"
            />
          </b-form-group>

          <b-form-group horizontal label="Role:">
            <b-form-radio-group
              id="role"
              v-model="$v.role.$model"
              name="role"
              :state="validateState('role')"
            >
              <div
                v-for="role in roleOptions"
                :key="role.value"
                class="d-inline-block"
              >
                <b-form-radio :value="role.value">
                  {{ role.text }}
                </b-form-radio>
              </div>
              <b-form-invalid-feedback :state="validateState('role')">
                {{ requiredInvalidFeedback }}
              </b-form-invalid-feedback>
            </b-form-radio-group>
          </b-form-group>
          <template v-if="role === 'MSS_ADMIN'">
            <b-form-group
              horizontal
              :invalid-feedback="requiredInvalidFeedback"
              label="Society"
            >
              <b-form-select
                v-model="$v.societyId.$model"
                :options="$store.state.societyOptions"
                :state="validateState('societyId')"
              />
            </b-form-group>
          </template>
          <mss-user-edit-form
            v-else-if="role === 'MSS_USER'"
            ref="userExtraFields"
          />
          <non-mss-user-edit-form
              v-else-if="role === 'NON_MSS_USER'"
              ref="userExtraFields"
          />
          <recruiter-form
            v-else-if="role === 'MSS_RECRUITER'"
            ref="userExtraFields"
          >
            <b-form-group
              slot="email-input"
              label="Email"
              label-class="v2_font_size_20 v2_color_med_gray required"
              :invalid-feedback="emailInvalidFeedback"
            >
              <b-form-input
                id="email"
                class="rounded-0 v2_color_primary_navy"
                v-model="$v.email.$model"
                :state="validateState('email')"
              />
            </b-form-group>
          </recruiter-form>
        </b-col>
      </b-row>
    </b-form>
  </b-container>
</template>

<script>
import Api from '@/providers/Api';
import { validationMixin } from 'vuelidate';
import { required, email, minLength, sameAs } from 'vuelidate/lib/validators';
import {
  ROLE_GM_ADMIN,
  ROLE_MSS_ADMIN,
  ROLE_MSS_RECRUITER,
} from '@/helpers/rbacHelper';
import MssUserEditForm from '@/components/users/forms/MssUserEditForm';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import RecruiterForm from '@/components/recruiter/form/RecruiterForm';
import NonMssUserEditForm from "@/components/users/forms/NonMssUserEditForm";

export default {
  name: 'UserAdd',
  mixins: [validationMixin],
  components: {
    NonMssUserEditForm,
    MssUserEditForm,
    RecruiterForm,
  },
  data: function () {
    return {
      email: null,
      password: null,
      confirmPassword: null,
      role: null,
      societyId: null,
      membershipTypeId: null,
      saveLoading: false,
      sendEmail: 1,
    };
  },
  validations: function () {
    const validation = {
      email: {
        required,
        email,
      },
      password: {
        required,
        minLength: minLength(8),
      },
      confirmPassword: {
        required,
        sameAsPassword: sameAs('password'),
      },
      role: {
        required,
      },
      societyId: {},
    };
    if (this.role === ROLE_MSS_ADMIN) {
      validation.societyId = { required };
    }
    return validation;
  },
  computed: {
    emailInvalidFeedback: function () {
      return !this.$v.email.email
        ? 'Email must be a valid email.'
        : 'Email is required';
    },
    passwordInvalidFeedback: function () {
      return !this.$v.password.minLength
        ? 'Password must be at least 8 characters'
        : 'This field is required';
    },
    confirmPasswordInvalidFeedback: function () {
      return !this.$v.confirmPassword.sameAsPassword
        ? 'Passwords must match'
        : 'This field is required';
    },
    requiredInvalidFeedback: function () {
      return 'This field is required.';
    },
    roleOptions: function () {
      return this.$store.state.roleOptions;
    },
  },
  methods: {
    onSaveHandle: function () {
      this.$v.$touch();
      this.$refs.userExtraFields && this.$refs.userExtraFields.validate();
      const extraFieldsError =
        this.$refs.userExtraFields &&
        typeof this.$refs.userExtraFields.hasErrors === 'function' &&
        this.$refs.userExtraFields.hasErrors();
      if (this.$v.$anyError || extraFieldsError) {
        return;
      }
      const extraData =
        this.$refs.userExtraFields &&
        typeof this.$refs.userExtraFields.getData === 'function'
          ? this.$refs.userExtraFields.getData()
          : {};
      const newUser = {
        email: this.email,
        password: this.password,
        confirm_password: this.confirmPassword,
        roles: [this.role],
        send_email: this.sendEmail,
        society_id:
          this.role !== ROLE_GM_ADMIN && this.role !== ROLE_MSS_RECRUITER
            ? this.societyId
            : null,
        ...extraData,
      };
      this.role === ROLE_MSS_RECRUITER
        ? this.saveNewRecruiter(newUser)
        : this.saveNewUser(newUser);
    },
    saveNewUser: async function (userData) {
      this.saveLoading = true;
      try {
        await Api.createUser(userData);
        this.$router.back();
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.saveLoading = false;
      }
    },
    saveNewRecruiter: async function (recruiterData) {
      this.saveLoading = true;
      try {
        await Api.createRecruiter(recruiterData);
        this.$router.back();
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.saveLoading = false;
      }
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
  },
};
</script>

<style scoped></style>
